import apiClient from './apiClient';
import {
  FlagQuestionRequest,
  ReviewQuestionRequest,
  DismissFlagRequest,
  FlaggedQuestionDetails,
  QuestionReviewHistoryItem,
  FlaggedQuestionStats,
  QuestionImpactPreview,
  QuestionReviewResponse,
  ApiResponse,
} from '../types';

/**
 * Question Review Service
 * Provides API methods for flagging questions and managing reviews
 */

/**
 * Flag a question for manual review
 */
export const flagQuestion = async (
  questionId: number,
  request: FlagQuestionRequest
): Promise<void> => {
  try {
    const response = await apiClient.post<ApiResponse<void>>(
      `/questions/${questionId}/flag`,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to flag question');
    }
  } catch (error) {
    console.error('Error flagging question:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to flag question for review');
  }
};

/**
 * Get all flagged questions (admin/teacher only)
 */
export const getFlaggedQuestions = async (pendingOnly: boolean = false): Promise<FlaggedQuestionDetails[]> => {
  try {
    const response = await apiClient.get<ApiResponse<FlaggedQuestionDetails[]>>(
      `/questions/flagged${pendingOnly ? '?pending=true' : ''}`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch flagged questions');
    }

    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching flagged questions:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch flagged questions');
  }
};

/**
 * Get statistics about flagged questions (admin/teacher only)
 */
export const getFlaggedQuestionStats = async (): Promise<FlaggedQuestionStats> => {
  try {
    const response = await apiClient.get<ApiResponse<FlaggedQuestionStats>>(
      '/questions/flagged/stats'
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch flagged question statistics');
    }

    return response.data.data || { totalFlagged: 0, pendingReview: 0, reviewed: 0 };
  } catch (error) {
    console.error('Error fetching flagged question stats:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch flagged question statistics');
  }
};

/**
 * Review a flagged question and optionally update its answer (admin/teacher only)
 */
export const reviewQuestion = async (
  questionId: number,
  request: ReviewQuestionRequest
): Promise<QuestionReviewResponse> => {
  try {
    const response = await apiClient.put<QuestionReviewResponse>(
      `/questions/${questionId}/review`,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to review question');
    }

    return response.data;
  } catch (error) {
    console.error('Error reviewing question:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to review question');
  }
};

/**
 * Dismiss a flag without changing the question answer (admin/teacher only)
 */
export const dismissFlag = async (
  questionId: number,
  request: DismissFlagRequest
): Promise<void> => {
  try {
    const response = await apiClient.put<ApiResponse<void>>(
      `/questions/${questionId}/dismiss-flag`,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to dismiss flag');
    }
  } catch (error) {
    console.error('Error dismissing flag:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to dismiss flag');
  }
};

/**
 * Get review history for a specific question
 */
export const getQuestionReviewHistory = async (questionId: number): Promise<QuestionReviewHistoryItem[]> => {
  try {
    const response = await apiClient.get<ApiResponse<QuestionReviewHistoryItem[]>>(
      `/questions/${questionId}/review-history`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch review history');
    }

    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching review history:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch review history');
  }
};

/**
 * Preview the impact of changing a question's answer (admin/teacher only)
 */
export const previewQuestionImpact = async (
  questionId: number,
  newAnswerKey: string
): Promise<QuestionImpactPreview> => {
  try {
    const response = await apiClient.get<ApiResponse<QuestionImpactPreview>>(
      `/questions/${questionId}/impact-preview`,
      {
        params: { newAnswerKey },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to preview impact');
    }

    return response.data.data || {
      impactStats: { totalAttempts: 0, uniqueUsers: 0, completedAttempts: 0, inProgressAttempts: 0 },
      preview: { totalAffectedAttempts: 0, potentialScoreChanges: [] },
    };
  } catch (error) {
    console.error('Error previewing impact:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to preview impact');
  }
};

/**
 * Check if a question is flagged for review
 */
export const isQuestionFlagged = (question: { flaggedForReview?: boolean }): boolean => {
  return question.flaggedForReview === true;
};

/**
 * Check if a question has been reviewed
 */
export const isQuestionReviewed = (question: { reviewedAt?: Date }): boolean => {
  return question.reviewedAt != null;
};

/**
 * Format user name for display
 */
export const formatUserName = (user: { firstName: string; lastName: string }): string => {
  return `${user.firstName} ${user.lastName}`;
};

/**
 * Format score change for display
 */
export const formatScoreChange = (scoreChange: number): string => {
  const sign = scoreChange > 0 ? '+' : '';
  return `${sign}${scoreChange}%`;
};

/**
 * Get color class for score change
 */
export const getScoreChangeColor = (scoreChange: number): string => {
  if (scoreChange > 0) return 'text-green-600';
  if (scoreChange < 0) return 'text-red-600';
  return 'text-gray-600';
};

export default {
  flagQuestion,
  getFlaggedQuestions,
  getFlaggedQuestionStats,
  reviewQuestion,
  dismissFlag,
  getQuestionReviewHistory,
  previewQuestionImpact,
  isQuestionFlagged,
  isQuestionReviewed,
  formatUserName,
  formatScoreChange,
  getScoreChangeColor,
};